import { useState, useEffect } from 'react'
import { api } from '../api'
import { useAuth } from '../AuthContext'
import RinaContextCard from '../components/RinaContextCard'
import { useTheme } from '../theme'
import PageHub from '../components/PageHub'

/**
 * Page « Notifications » du Compte (prototype v7.4).
 *
 * Elle sort de Paramètres, ou l'interrupteur push etait range : le prototype en
 * fait une entree a part entiere de « Mon espace », et c'est la que les gens la
 * cherchent.
 *
 * DEUX NIVEAUX, et il faut les distinguer :
 * - **Autoriser les notifications** : reel. C'est la permission du navigateur +
 *   l'abonnement push (`PushSubscription`), deja implemente cote backend.
 * - **Les quatre sujets** du prototype (tournees, marche, favoris, RINA) :
 *   affiches « Bientot ». Le backend ne stocke pas encore de preference PAR
 *   SUJET — les proposer donnerait des interrupteurs qui ne commandent rien.
 *   A ouvrir avec le reste (cf. docs/BACKLOG-COMPTE.md).
 */

const SUJETS = [
  { k: 'tournees', l: 'Ouverture des tournées', d: 'Quand une tournée ouvre à la commande' },
  { k: 'marche', l: 'Rappel marché du samedi', d: 'La veille du marché' },
  { k: 'favoris', l: 'Retour d’un favori en stock', d: 'Un produit suivi revient' },
  { k: 'rina', l: 'Messages de RINA', d: 'Réponses et rappels' },
]

function Interrupteur({ actif }) {
  return (
    <span className="w-11 h-[26px] rounded-full relative flex-shrink-0 transition-colors"
      style={{ background: actif ? 'rgb(var(--c-teal))' : 'rgba(7,62,61,0.18)' }}>
      <span className="absolute top-[3px] w-5 h-5 rounded-full bg-white transition-[left]"
        style={{ left: actif ? 21 : 3, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </span>
  )
}

function ContenuNotifications() {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = isWeb3 ? 'text-white/60' : 'text-ink/60'
  const cardBg = isWeb3 ? 'bg-white/5 border-white/10' : 'bg-white/70 border-ink/10'

  const [statut, setStatut] = useState('loading') // loading | granted | denied | default | unsupported
  const [abonne, setAbonne] = useState(false)

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setStatut('unsupported')
      return
    }
    setStatut(Notification.permission)
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription().then((sub) => setAbonne(!!sub)))
      .catch(() => {})
  }, [])

  const basculer = async () => {
    if (statut === 'unsupported' || statut === 'denied') return

    if (abonne) {
      try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        if (sub) {
          await sub.unsubscribe()
          await api.notifications.unsubscribe(sub.endpoint).catch(() => {})
        }
        setAbonne(false)
      } catch (e) {
        console.error('Erreur desabonnement:', e)
      }
      return
    }

    try {
      const permission = await Notification.requestPermission()
      setStatut(permission)
      if (permission !== 'granted') return

      const { publicKey } = await api.notifications.getVapidKey()
      if (!publicKey) return

      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: publicKey })
      const json = sub.toJSON()
      await api.notifications.subscribe({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        userAgent: navigator.userAgent,
      }, token)
      setAbonne(true)
    } catch (e) {
      console.error('Erreur souscription push:', e)
    }
  }

  const sousTitre = statut === 'unsupported' ? 'Non disponible sur cet appareil'
    : statut === 'denied' ? 'Bloquées par le navigateur'
      : abonne ? 'Activées sur cet appareil' : 'Désactivées'

  return (
    <>
      <RinaContextCard
       
        title="Notifications"
        message="Je vous préviens quand quelque chose vous concerne — sans vous inonder."
      />

      <div className={`rounded-[22px] border overflow-hidden animate-fade-up ${cardBg}`}>
        <button
          onClick={basculer}
          disabled={statut === 'unsupported' || statut === 'denied'}
          className="w-full flex items-center gap-3 px-4 py-[15px] text-left active:scale-[0.99] transition-transform disabled:active:scale-100"
        >
          <span className="w-[34px] h-[34px] rounded-xl flex-shrink-0 flex items-center justify-center text-[17px]"
            style={{ background: 'rgba(20,184,166,0.12)' }}>🔔</span>
          <span className="flex-1 min-w-0">
            <span className={`block text-sm font-semibold ${tc}`}>Autoriser les notifications</span>
            <span className={`block text-[11px] ${ts}`}>{sousTitre}</span>
          </span>
          <Interrupteur actif={abonne} />
        </button>
      </div>

      {statut === 'denied' && (
        <p className={`text-[11px] leading-relaxed mt-2.5 px-1 ${ts}`}>
          Votre navigateur les bloque pour ce site. Réactivez-les dans ses réglages,
          puis revenez sur cette page.
        </p>
      )}

      <p className="text-[11px] font-bold uppercase tracking-[0.16em] mx-0.5 mb-2.5 mt-5 text-teal">
        Ce que je vous envoie
      </p>
      <div className={`rounded-[22px] border overflow-hidden animate-fade-up ${cardBg}`} style={{ animationDelay: '0.08s' }}>
        {SUJETS.map((s, i) => (
          <div key={s.k}
            className={`flex items-center gap-3 px-4 py-[15px] ${
              i > 0 ? `border-t ${isWeb3 ? 'border-white/5' : 'border-ink/[0.07]'}` : ''
            }`}>
            <span className="flex-1 min-w-0">
              <span className={`block text-sm font-semibold ${tc}`}>{s.l}</span>
              <span className={`block text-[11px] ${ts}`}>{s.d}</span>
            </span>
            <span className="text-[10.5px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 flex-shrink-0 text-primary"
              style={{ background: 'rgba(244,137,83,0.14)', border: '1px solid rgba(244,137,83,0.3)' }}>
              Bientôt
            </span>
          </div>
        ))}
      </div>

      <div className={`rounded-2xl border mt-3.5 px-4 py-3 flex gap-2.5 items-start animate-fade-up ${cardBg}`}>
        <span className="text-[15px]">🦊</span>
        <p className={`text-xs leading-relaxed ${ts}`}>
          Le choix sujet par sujet arrive bientôt. En attendant, l’autorisation
          ci-dessus commande tout — et vous pouvez la couper à tout moment.
        </p>
      </div>
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageNotifications() {
  return <PageHub retour="account"><ContenuNotifications /></PageHub>
}

